import { ExportExploreTrendsStack } from '@data-visualization/trends-stack-chart/Types';

import { StackedChartResp } from '@data-visualization/stacked-chart/Types';
import { CategoryLeaderboardPgPage } from '@models/CategoryLeaderboard';
import { Country, DataType } from '@utils/pageConfig/Types';
import { ExportPerformanceBySegmentResp } from '@data-visualization/performance-analysis-line-chart/Types';

export const preprocessExploreLeaderboardResponse = (
  response: CategoryLeaderboardPgPage,
  countries: Country[],
  showPerfDataType?: string,
) => {
  if (showPerfDataType && showPerfDataType === DataType.COUNTRIES) {
    mapCountryNamesForLeaderboardResponse(response, countries);
  }
};

const mapCountryNamesForLeaderboardResponse = (response: CategoryLeaderboardPgPage, countries: Country[]) => {
  const {
    groupingKpis,
    leaderboardKpis: { content },
  } = response;

  //Map each country code to its name
  content.forEach((segment) => {
    const countryName = countries.find((country) => country.id === segment.segmentOrPg)?.name;
    segment.segmentOrPg = countryName ?? segment.segmentOrPg;
  });

  //Perform country id to country name mapping for each Group
  groupingKpis.forEach((group) => {
    const { groupDetails, groupKpis } = group;

    //Map each groupDetails segment array to its respective country name
    const countriesName = groupDetails.segmentOrPgValue.map(
      (countrySegment) => countries.find((country) => country.id === countrySegment)?.name ?? countrySegment,
    );
    groupDetails.segmentOrPgValue = countriesName;

    //Map each groupKpis country id to its respective country name
    groupKpis.forEach((groupKpi) => {
      const countryName = countries.find((country) => country.id === groupKpi.segmentOrPg)?.name;
      groupKpi.segmentOrPg = countryName ?? groupKpi.segmentOrPg;
    });
  });
};

const shouldMapCountryNames = (showPerfDataType?: string) => showPerfDataType === DataType.COUNTRIES;

export const preprocessExploreTrendsResponse = (
  response: ExportPerformanceBySegmentResp,
  countries: Country[],
  showPerfDataType?: string,
) => {
  if (shouldMapCountryNames(showPerfDataType)) {
    mapCountryNamesForTrendsResponse(response, countries);
  }
};

export const preprocessExploreTrendsStackResponse = (
  response: ExportExploreTrendsStack,
  countries: Country[],
  showPerfDataType?: string,
) => {
  if (shouldMapCountryNames(showPerfDataType)) {
    mapCountryNamesForTrendsStackResponse(response, countries);
  }
};

const mapCountryNamesForTrendsResponse = (response: ExportPerformanceBySegmentResp, countries: Country[]) => {
  const { kpiDataDto } = response;
  kpiDataDto.forEach((data) => {
    data.brandName = countries.find((country) => country.id === data.brandName)?.name ?? data.brandName;
  });
};

const mapCountryNamesForTrendsStackResponse = (response: ExportExploreTrendsStack, countries: Country[]) => {
  response.series.forEach((seriesItem) => {
    seriesItem.kpiData.forEach((dataItem) => {
      dataItem.name = countries.find((country) => country.id === dataItem.name)?.name ?? dataItem.name;
    });
  });
};

export const preprocessExploreStackChartResponse = (
  response: StackedChartResp[],
  countries: Country[],
  showPerfDataType?: string,
  segmentBy?: string,
) => {
  if (showPerfDataType && showPerfDataType === DataType.COUNTRIES) {
    mapCountryNamesForShowPerfStackChartResponse(response, countries);
  } else if (segmentBy && segmentBy === DataType.COUNTRIES) {
    mapCountryNamesForSegmentByStackChartResponse(response, countries);
  }
};

export const mapCountryNamesForShowPerfStackChartResponse = (legendData: StackedChartResp[], countries: Country[]) => {
  legendData.forEach((data) => {
    const {
      share: { series },
    } = data;
    series.forEach((seriesData) => {
      seriesData.label = countries.find((country) => country.id === seriesData.label)?.name ?? seriesData.label;
    });
  });
};

export const mapCountryNamesForSegmentByStackChartResponse = (legendData: StackedChartResp[], countries: Country[]) => {
  legendData.forEach((data) => {
    data.segment = countries.find((country) => country.id === data.segment)?.name ?? data.segment;
  });
};
